<svelte:options customElement={{ tag: "bt-add-to-curation-modal", shadow: "none", }} />

<script lang="ts">
  import { onMount } from 'svelte';
  import { writable } from 'svelte/store';
  import { fly } from 'svelte/transition';
  import { default as CurationCheckbox } from '@/components/curations/CurationCheckbox.svelte';
  import { default as NewCurationItem } from '@/components/curations/NewCurationItem.svelte';
  import Modal from '$shared/modal/Modal.svelte';

  export let contentId;
  export let contentType;

  export let cancelText = 'Cancel';
  export let addToCurationModalTitle = 'Add to Curation';
  export let addToCurationTextEnter = 'Enter';
  export let addToCurationTextPlaceholder = 'New Curation';
  export let addToCurationListHeader = 'Your Curations';

  let curations = writable([]);
  let itemToCurate;
  
  const fetchCurationData = () => {
    const url = '/curations/add_to_curation';
    const curationData = {
      "curated_item": contentId,
      "content_type": contentType
    }

    fetch(url, {
      body: JSON.stringify(curationData),
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
      }
    })
    .then(response => response.json())
    .then(data => {
      curations = JSON.parse(JSON.stringify(data.collection));
      itemToCurate = JSON.parse(JSON.stringify(curationData));
    });
  }

  onMount(() => {
    fetchCurationData();
  });

	const defaultMessage = '';
	let feedbackMessage = defaultMessage;
	let closeMessageTimeout = setTimeout(clearMessage, 2000);

  async function addItemToCuration(curationId: string) {
    const updateCurationData = {
      "curatable_id": itemToCurate.curated_item,
      "curatable_type": itemToCurate.content_type
    }

    try {
      await fetch(`/curations/${curationId}/curated_items`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        body: JSON.stringify(updateCurationData)
      });
    } catch (error) {
      console.error(error);
    }
  }

  async function createNewCuration(name: string) {
    const newCurationData = {
      "curation": {
        "name": name
      }
    }

    try {
      const response = await fetch(`/curations`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          'Accept': 'application/json',
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        body: JSON.stringify(newCurationData),
      });

      const data = await response.json();
      const curationId = JSON.stringify(data.curation_id);

      return curationId;
    } catch (error) {
      console.error(error);
    }
	}

  function insertNewCuration(curationId: string, name: string) {
    const newCuration = {
      "id": parseInt(curationId),
      "name": name,
      "existing_id": parseInt(curationId)
    }
    curations = [newCuration, ...curations];
  }

	function handleAddToCuration(e: any) {
		let curationId = e.detail.id;

    addItemToCuration(curationId);

    setMessage('Added to curation');
	}

	function setMessage(msg: string) {
		feedbackMessage = msg;
		clearTimeout(closeMessageTimeout);
		closeMessageTimeout = setTimeout(clearMessage, 2000);
	}

	function clearMessage() {
		feedbackMessage = defaultMessage;
	}

	function handleRemoveFromCuration(e: any) {
		let curationId = e.detail.id;
    let curationItemId = e.detail.item_id;

    fetch(`/curations/${curationId}/curated_items/${curationItemId}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      }
    });

		setMessage('Removed from curation');
	}

	async function handleSubmit(e: any) {
    try {
      const curationId = await createNewCuration(e.detail.value);

      await addItemToCuration(curationId);

      setMessage('New curation created');

      insertNewCuration(curationId, e.detail.value);
    } catch (error) {
      console.error(error);
    }
	}
</script>

{#if curations && itemToCurate}
  <Modal modalTitle={addToCurationModalTitle} closeButtonText={cancelText}>
    <div class="add-to-curations" slot="modal-contents">
      <NewCurationItem 
        {addToCurationTextEnter}
        {addToCurationTextPlaceholder}
        on:newCurationSubmit={handleSubmit} 
      />
      <div class="add-to-curations__list">
        <h3>{addToCurationListHeader}</h3>
        <div class="add-to-curations__list__inner">
          {#each curations as curation (curation.id)}
            <CurationCheckbox
              on:check={handleAddToCuration}
              on:uncheck={handleRemoveFromCuration}
              allCurations={curations}
              curation={curation}
              i={curation.id}
            />
          {/each}
        </div>
      </div>
      <div class="add-to-curations__message">
        <div class="add-to-curations__message__inner">
          {#key feedbackMessage}
            <p in:fly={{ y: 20 }} out:fly={{ y: -20 }}>{feedbackMessage}</p>
          {/key}
        </div>
      </div>
    </div>
  </Modal>
{/if}

<style>
  .add-to-curations {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.4');
  }
  .add-to-curations__list {
    position: relative;
  }
  .add-to-curations__list h3 {
    font-size: theme('fontSize.lg');
    font-weight: 600;
    margin-top:  theme('margin.2');
    margin-bottom: theme('margin.2');
  }
  .add-to-curations__list__inner {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.2');
    height: 100%;
    max-height: 10rem;
    overflow-y: auto;
    border: 1px solid theme('colors.neutral.400');
    border-radius: theme('borderRadius.sm');
    padding: theme('padding.2');
  }
  .add-to-curations__message {
    display: flex;
    & p {
      position: absolute;
      left: 0;
    }
  }
  .add-to-curations__message__inner {
    display: flex;
    position: relative;
    width: 100%;
    align-items: center;
    opacity: 0.5;
    font-style: italic;
    transition: all cubic-bezier(0.4, 0, 0.2, 1) 150ms;
  }
</style>